
import { inject, Injectable } from '@angular/core';
import { Http, QueryHandlerFor, RootContext } from '@skylab-angular-shared/core';
import { CompanyParametersQuery, CurrencyCollectionQuery, CurrencyItem, PagedCollectionQuery, PagedCollectionQueryResult, PagedCollectionQueryResultDTO } from '@skylab-shared/business';
import { HttpService, QueryHandler, RootContextBase, TaggedUrl, Tags, transformDTODate } from '@skylab-shared/core';
import { CalculationMethod, DdkState, EppState, InventoryDocumentKind, InventoryEffect, JpkVatState, PurchaseDocumentKind } from '@skylab-shared/dataModel';
import { Transform } from 'class-transformer';

export interface PurchaseCurrencyItem {
    precision: number;
    symbol: string;
}

export type PurchaseGridItem = {
    id: number;
    version: string;
    externalId: string;

    calculationMethod: CalculationMethod;
    communityTransactionId: string;
    currencyId: number;
    documentNumber: string;
    externalDocumentNumber: string;
    favourite: boolean;

    dueDate: Date;
    issueDate: Date;
    receiptDate: Date;
    vatDeductionDate: Date;

    kind: PurchaseDocumentKind;
    note: string;
    paymentMethodId: string;
    seller: string;

    baseCurrency: CurrencyItem;
    baseGrossValue: number;
    baseNetValue: number;

    currency: CurrencyItem;
    grossValue: number;
    netValue: number;

    ddkState: DdkState;
    expenseCurrency: PurchaseCurrencyItem;
    eppState: EppState;
    jpkVatState: JpkVatState;
    orderState: number;
    relatedInventoryGoodsReceiptDocument?: {
        documentId: number;
        kind: InventoryDocumentKind,
        number: string;
    };
    issueGoodsReceiptAdjustment: boolean;
    inventoryEffect: InventoryEffect;
    ksefHeaderId: number;
    ksefReferenceNumber: string;
    ksefHash: string;
    dateOfAssigningKsefNumber?: Date,
    leftToPay: number;
};

class PurchaseGridItemDTO {
    public readonly calculationMethod: CalculationMethod;
    public readonly communityTransactionId: string;
    public readonly currencyId: number;
    public readonly ddkState: DdkState;
    public readonly documentNumber: string;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly dueDate: Date;
    public readonly externalDocumentNumber: string;
    public readonly favourite: boolean;
    public readonly eppState: EppState;
    public readonly id: number;
    public readonly externalId: string;
    public readonly issueDate: string;
    public readonly jpk_vatState: JpkVatState;
    public readonly leftToPay: number;
    public readonly kind: PurchaseDocumentKind;
    public readonly note: string;
    public readonly paymentMethodId: string;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly receiptDate: Date;
    public readonly seller: string;
    public readonly totalBaseCurrencyGrossValue: number;
    public readonly totalBaseCurrencyNetValue: number;
    public readonly totalGrossValue: number;
    public readonly totalNetValue: number;
    @Transform(transformDTODate, { toClassOnly: true })  public readonly vatDeductionDate: Date;
    @Transform(transformDTODate, { toClassOnly: true })  public readonly dateOfAssigningKsefNumber: Date;
    public readonly vatTable: string;
    public readonly version: string;
    public readonly relatedInventoryGoodsReceiptDocument?: {
        readonly documentId: number;
        readonly kind: InventoryDocumentKind,
        readonly number: string;
    };
    public readonly issueGoodsReceiptAdjustment: boolean;
    public readonly inventoryEffect: InventoryEffect;
    public readonly ksefHeaderId: number;
    public readonly ksefReferenceNumber: string;
    public readonly ksefHash: string;
}

export class PurchaseGridQuery extends PagedCollectionQuery<PurchaseGridItem> { }

@Injectable({ providedIn: 'root'})
@QueryHandlerFor(PurchaseGridQuery)
export class PurchaseGridQueryHandler implements QueryHandler<PurchaseGridQuery, PagedCollectionQueryResult<PurchaseGridItem>> {
    #Url = `${TaggedUrl.join('purchase', [Tags.Api])}`;
    #http = inject(Http) as HttpService;
    #rootContext = inject(RootContext) as any as RootContextBase;

    public async handleAsync(query: PurchaseGridQuery): Promise<PagedCollectionQueryResult<PurchaseGridItem>> {
        const { data } = await this.#http.get(this.#Url, {
            params: { ...query.params },
            dtoObject: new PagedCollectionQueryResultDTO<PurchaseGridItemDTO>(PurchaseGridItemDTO)
        });

        const items = await Promise.all(data.items.map<Promise<PurchaseGridItem>>(async item => {
            const companyParameters = await this.#rootContext.getAsync(CompanyParametersQuery);

            const [itemCurrency, baseCurrency] = await Promise.all([
                this.#rootContext.firstAsync(CurrencyCollectionQuery, { id: item.currencyId }),
                this.#rootContext.firstAsync(CurrencyCollectionQuery, { symbol: companyParameters.systemCurrency })
            ]);

            return {
                baseCurrency: baseCurrency,
                baseGrossValue: item.totalBaseCurrencyGrossValue,
                baseNetValue: item.totalBaseCurrencyNetValue,
                calculationMethod: item.calculationMethod,
                communityTransactionId: item.communityTransactionId,
                currency: itemCurrency,
                currencyId: item.currencyId,
                ddkState: item.ddkState,
                documentNumber: item.documentNumber,
                dueDate: item.dueDate,
                leftToPay: item.leftToPay,
                expenseCurrency: {
                    precision: itemCurrency.precision,
                    symbol: itemCurrency.symbol
                },
                externalDocumentNumber: item.externalDocumentNumber,
                favourite: item.favourite,
                eppState: item.eppState,
                grossValue: item.totalGrossValue,
                id: item.id,
                issueDate: new Date(item.issueDate),
                jpkVatState: item.jpk_vatState,
                kind: item.kind,
                netValue: item.totalNetValue,
                note: item.note,
                orderState: 0,
                paymentMethodId: item.paymentMethodId,
                receiptDate: item.receiptDate,
                seller: item.seller,
                vatDeductionDate: item.vatDeductionDate,
                vatTable: item.vatTable,
                version: item.version,
                relatedInventoryGoodsReceiptDocument: item.relatedInventoryGoodsReceiptDocument,
                issueGoodsReceiptAdjustment: item.issueGoodsReceiptAdjustment,
                inventoryEffect: item.inventoryEffect,
                ksefHeaderId: item.ksefHeaderId,
                ksefReferenceNumber: item.ksefReferenceNumber,
                externalId: item.externalId,
                ksefHash: item.ksefHash,
                dateOfAssigningKsefNumber: item.dateOfAssigningKsefNumber
            };
        }));

        return {
            items: items,
            totalItemsCount: data.totalItemsCount
        };
    }
}
